import posthog from "posthog-js";
import type { ErrorEvent, Exception } from "@sentry/browser";

/** Sentry's mechanism type for events synthesized by captureConsoleIntegration. */
const CONSOLE_MECHANISM = "console";

const AUTH_API_ERROR_TYPE = "AuthApiError";

/**
 * Auth failures Supabase reports when a stored session cannot be reused. Only
 * non-network messages belong here: a failed auth *request* is a network failure
 * and is handled by that rule first, which runs before this one.
 *
 * Matched as a substring so the whole "Invalid Refresh Token: ..." family is
 * covered — Session Expired, Not Found, Already Used.
 */
const EXPECTED_SESSION_REUSE_ERRORS = ["Invalid Refresh Token"];

/**
 * Outcomes that come from the person or their device rather than from a bug,
 * matched against the whole exception message. Each is surfaced to the user in
 * the UI where it matters; none is something we can fix.
 */
const EXPECTED_EXACT_MESSAGES = [
  // Google sign-in: the person closed or cancelled the flow, or the popup was
  // blocked or abandoned.
  "Popup closed",
  "Failed to open popup",
  "OAuth timeout",
  "The user canceled the sign-in flow.",
  "access_denied",
  // Native share sheet dismissed.
  "Share canceled",
  // The Live Update plugin rejects when two syncs overlap. The overlap is
  // harmless (the other sync does the work), and this rejection is raised by
  // the native layer with no stack, so the caller cannot catch it.
  "Sync is already in progress.",
];

/** Same idea, for messages that arrive wrapped in extra platform text. */
const EXPECTED_MESSAGE_FRAGMENTS = [
  // iOS ASAuthorizationError 1001: the person dismissed the sign-in sheet.
  "AuthorizationError error 1001",
  // Injected by Outlook SafeLinks and similar link scanners that probe the page
  // with a bot; there is no real user to affect.
  "Object Not Found Matching Id:",
  // The request went out without a session, which means the person's session
  // ended; the route guards and the SIGNED_OUT handler send them to sign in.
  "Missing authentication token",
];

/**
 * How each engine words "the request never reached a server". None of these
 * carry an HTTP status, which is what separates them from server errors: there
 * was no response at all.
 */
const NETWORK_FAILURE_MESSAGES = [
  "Failed to fetch", // Chrome/Firefox
  "Load failed", // Safari
  "NetworkError when attempting to fetch resource.",
  "The Internet connection appears to be offline.", // WebKit
  "The network connection was lost.", // WebKit
  "Network request failed",
  "The request timed out.", // WebKit
];

const UUID_PATTERN =
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

const REQUEST_BREADCRUMB_CATEGORIES = ["fetch", "xhr"];

/** PostHog event carrying the rate that replaces the dropped Sentry issues. */
const NETWORK_FAILURE_EVENT = "network_request_failed";

type RequestKind = "trpc" | "supabase" | "analytics" | "other" | "unknown";

const matchesAnyException = (
  event: ErrorEvent,
  predicate: (value: Exception) => boolean,
) => event.exception?.values?.some(predicate) ?? false;

const hasMessageFrom = (event: ErrorEvent, messages: string[]) =>
  matchesAnyException(event, (value) =>
    messages.some((message) => (value.value ?? "").includes(message)),
  );

const firstExceptionValue = (event: ErrorEvent) =>
  event.exception?.values?.[0]?.value ?? "unknown";

/**
 * True when the request never reached a server. Covers both the raw rejection
 * and its wrappers: one dropped request can surface as `TypeError: Failed to
 * fetch` or as the `TRPCClientError` that wrapped it, depending on where it was
 * caught, and both land in the same Sentry issue.
 */
export const isNetworkFetchFailure = (event: ErrorEvent) =>
  hasMessageFrom(event, NETWORK_FAILURE_MESSAGES);

/**
 * GoTrueClient calls console.error when a stored session cannot be reused, which
 * happens routinely on the sign-in page: that unusable session is exactly why
 * the user is being asked to sign in. The library handles it by emitting
 * INITIAL_SESSION with no session, and replays confirm affected users go on to
 * sign in and reach their destination.
 *
 * captureConsoleIntegration promotes every console.error to an error-level
 * issue, so these arrive looking like crashes — over 750 events from 530 users
 * for Sentry SAILE-WEB-9Y alone.
 *
 * Deliberately narrow. It requires all three of: the console mechanism, the
 * AuthApiError type Supabase throws, and a known-expected message. An
 * app-thrown auth error keeps a non-console mechanism and still reports. The
 * check uses the exception type rather than a stack frame because `beforeSend`
 * runs before sourcemaps are applied, so frames only name minified bundles.
 */
export const isExpectedSupabaseSessionReuseFailure = (event: ErrorEvent) => {
  const isConsoleCaptured = matchesAnyException(
    event,
    (value) => value.mechanism?.type === CONSOLE_MECHANISM,
  );
  const isAuthApiError = matchesAnyException(
    event,
    (value) => value.type === AUTH_API_ERROR_TYPE,
  );

  return (
    isConsoleCaptured &&
    isAuthApiError &&
    hasMessageFrom(event, EXPECTED_SESSION_REUSE_ERRORS)
  );
};

/** True for user- or device-caused outcomes listed above. */
export const isExpectedUserOrDeviceOutcome = (event: ErrorEvent) =>
  matchesAnyException(event, (value) => {
    const message = value.value ?? "";
    return (
      EXPECTED_EXACT_MESSAGES.includes(message) ||
      EXPECTED_MESSAGE_FRAGMENTS.some((fragment) => message.includes(fragment))
    );
  });

const findFailedRequestUrl = (event: ErrorEvent): string | undefined => {
  const breadcrumbs = event.breadcrumbs ?? [];
  // Most recent first: the failing request is the last one recorded.
  for (let index = breadcrumbs.length - 1; index >= 0; index -= 1) {
    const breadcrumb = breadcrumbs[index];
    if (!REQUEST_BREADCRUMB_CATEGORIES.includes(breadcrumb.category ?? "")) {
      continue;
    }
    const url = breadcrumb.data?.url;
    if (typeof url === "string" && url.length > 0) {
      return url;
    }
  }
  return undefined;
};

const classifyRequest = (url: string): RequestKind => {
  if (url.includes("/trpc/")) {
    return "trpc";
  }
  if (url.includes("supabase")) {
    return "supabase";
  }
  if (url.includes("posthog") || url.includes("sentry")) {
    return "analytics";
  }
  return "other";
};

/**
 * Host plus path with UUIDs collapsed, so the property stays low-cardinality —
 * `my.saileapp.com/provider/jobs/:id` rather than one value per job.
 */
const normalizeRequestTarget = (rawUrl: string) => {
  try {
    // Breadcrumb URLs are usually absolute, but resolve relative ones against
    // the page when there is a window to read. Guarded so a missing `window`
    // cannot turn every target into "unparseable".
    const base =
      typeof window === "undefined" ? undefined : window.location.href;
    const url = new URL(rawUrl, base);
    return `${url.host}${url.pathname.replace(UUID_PATTERN, ":id")}`;
  } catch {
    return "unparseable";
  }
};

/**
 * Counts a dropped network failure in PostHog.
 *
 * A rate is the only useful form of this signal. Individual reports cannot
 * distinguish a user losing connectivity from our own config breaking, because
 * the browser surfaces both identically — but the shapes differ in aggregate: a
 * CORS, CSP, TLS, or edge-rule regression breaks everyone at once and shows as a
 * cliff, while connectivity loss is a permanent trickle. `browser_online` and
 * `page_visibility` let one query separate the two.
 */
const recordNetworkFailure = (event: ErrorEvent) => {
  const url = findFailedRequestUrl(event);
  const requestKind: RequestKind = url ? classifyRequest(url) : "unknown";

  // Never count a failed analytics request. This counter is itself a request to
  // PostHog, so counting its own failures could feed back on itself.
  if (requestKind === "analytics") {
    return;
  }

  try {
    posthog.capture(NETWORK_FAILURE_EVENT, {
      request_kind: requestKind,
      request_target: url ? normalizeRequestTarget(url) : "unknown",
      error_message: firstExceptionValue(event),
      browser_online: navigator.onLine !== false,
      page_visibility:
        typeof document === "undefined" ? "unknown" : document.visibilityState,
    });
  } catch {
    // PostHog's provider mounts after Sentry initializes, so very early failures
    // may not be counted. Losing a count must never break error reporting.
  }
};

/**
 * Single entry point for Sentry's `beforeSend`. Returning null discards the
 * event.
 *
 * Network-level failures are dropped wholesale and counted in PostHog instead.
 * They are unactionable individually — 316 events across 18 issue groups in 30
 * days yielded no fixable bug — and the outage class they could reveal is better
 * detected as a rate. The network check runs first so Supabase's own failed auth
 * requests are counted alongside every other dropped request.
 */
export const filterSentryEvent = (event: ErrorEvent): ErrorEvent | null => {
  if (isNetworkFetchFailure(event)) {
    recordNetworkFailure(event);
    return null;
  }

  if (
    isExpectedSupabaseSessionReuseFailure(event) ||
    isExpectedUserOrDeviceOutcome(event)
  ) {
    return null;
  }

  return event;
};
